import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  Line,
} from 'recharts';
import styles from '../styles/CompareSnakes.module.css';

// Helper to format dates as MM/DD/YYYY.
const formatDate = (dateStr) => {
  if (typeof dateStr !== 'string') return dateStr;
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};
// Centered Y‑Axis label component.
const CenteredYAxisLabel = ({ viewBox, value }) => {
  const { x, y, height } = viewBox;
  const cx = x + 10;
  const cy = y + height / 2;
  return (
    <text
      x={cx}
      y={cy}
      fill="grey"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90, ${cx}, ${cy})`}
      style={{ fontSize: 15 }}
    >
      {value}
    </text>
  );
};

// Custom tooltip showing all series values.
// const CustomTooltip = ({ active, payload, nameMapping, groupColors }) => {
//   if (!active || !Array.isArray(payload) || payload.length === 0) return null;
//   const entry = payload[0].payload;
//   const labelValue = entry.game != null ? `Game #${entry.game}` : formatDate(entry.date);
//
//   return (
//     <div
//       style={{
//         backgroundColor: 'rgba(255,255,255,0.9)',
//         padding: '10px',
//         border: '1px solid #ccc',
//         borderRadius: '4px',
//         pointerEvents: 'none',
//         zIndex: 9999,
//       }}
//     >
//       <p style={{ margin: 0, fontWeight: 'bold' }}>{labelValue}</p>
//       {payload.map((item) => {
//         const key = item.dataKey.split('.')[1];
//         const displayName = nameMapping[key] || key;
//         return (
//           <p key={key} style={{ margin: 0, color: groupColors[key]?.color || '#000' }}>
//             {`${displayName}: ${item.value}`}
//           </p>
//         );
//       })}
//     </div>
//   );
// };
// const CustomTooltip = ({ active, payload, nameMapping, groupColors }) => {
//   if (!active || !Array.isArray(payload) || payload.length === 0) return null;
//   const entry = payload[0].payload;
//   const labelValue = entry.game != null
//     ? `Game #${entry.game}`
//     : formatDate(entry.date);
//
//   // Build a unique list by dataKey
//   const seen = new Set();
//   const unique = payload.filter(item => {
//     if (seen.has(item.dataKey)) return false;
//     seen.add(item.dataKey);
//     return true;
//   });
//
//   return (
//     <div
//         style={{
//           backgroundColor: 'rgba(255,255,255,0.9)',
//           padding: '10px',
//           border: '1px solid #ccc',
//           borderRadius: '4px',
//           pointerEvents: 'none',
//           zIndex: 9999,
//         }}
//     >
//       <p style={{ margin: 0, fontWeight: 'bold' }}>{labelValue}</p>
//       {unique.map(item => {
//         const key = item.dataKey.split('.')[1];
//         const displayName = nameMapping[key] || key;
//         return (
//           <p key={item.dataKey} style={{ margin: 0, color: groupColors[key]?.color }}>
//             {`${displayName}: ${item.value}`}
//           </p>
//         );
//       })}
//     </div>
//   );
// };
const CustomTooltip = ({ active, payload, nameMapping, groupColors }) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const entry = payload[0].payload;
  const statDataKey = payload[0].dataKey.split('.')[0]; // "teams" or "players"

  // 1) Header + date
  const header = entry.game != null
    ? `Game #${entry.game}`
    : formatDate(entry.date);

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.9)',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: 9999,
    }}>
      <p style={{ margin: 0, fontWeight: 'bold' }}>{header}</p>
      <p style={{ margin: '2px 0', color: '#555' }}>{formatDate(entry.date)}</p>

      {/* 2) Multi-team grouping */}
      {entry.metadata
        ? Object.keys(entry.metadata).map(key => {
            const meta = entry.metadata[key];
            const displayName = nameMapping[key] || key;
            const value = entry[statDataKey]?.[key];
            return (
              <React.Fragment key={key}>
                {/* score_string first */}
                <p
                  style={{
                    margin: '2px 0',
                    fontStyle: 'bold',
                    color: 'black',
                    // color: groupColors[key]?.color,
                  }}
                >
                  {`${displayName}: ${meta.score_string}`}
                </p>
                {/* then the stat value */}
                <p
                  style={{
                    margin: '2px 0',
                    color: groupColors[key]?.color,
                  }}
                >
                  {`${displayName}: ${value}`}
                </p>
              </React.Fragment>
            );
          })
        : (
          /* 3) Single-team (or fallback) */
          <>
            {entry.score_string && (
              <p style={{ margin: '2px 0', fontStyle: 'italic' }}>
                {entry.score_string}
              </p>
            )}
            {/* still show the series value(s) */}
            {payload
              .filter((item, i) => payload.findIndex(p => p.dataKey === item.dataKey) === i)
              .map(item => {
                const key = item.dataKey.split('.')[1];
                const displayName = nameMapping[key] || key;
                return (
                  <p
                    key={item.dataKey}
                    style={{ margin: 0, color: groupColors[key]?.color }}
                  >
                    {`${displayName}: ${item.value}`}
                  </p>
                );
              })}
          </>
        )}
    </div>
  );
};

const SnakeChart = ({
  data: incomingData,
  groupColors,
  yAxisLabel,
  playerNames,
  teamNames,
  season,
  playerInfo,
  xAxisType,
  logo_points = false,
}) => {
  const [activeLine, setActiveLine] = useState(null);

  // 1) Normalize and memoize raw data
  const rawData = useMemo(() => (Array.isArray(incomingData) ? incomingData : []), [incomingData]);

  // 2) Determine if using game axis
  const isGameAxis = xAxisType === 'game';

  const isTeamGrouping = rawData[0]?.hasOwnProperty('teams');

  const data = useMemo(() => {
  if (!isGameAxis) return rawData;
  const map = {};
  rawData.forEach((d) => {
    const g = d.game;
    if (!map[g]) {
      // start with whichever group you're in
      map[g] = {
        ...d,
        players: isTeamGrouping ? {} : { ...d.players },
        teams:  isTeamGrouping ? { ...d.teams } : {},
      };
    } else {
      if (isTeamGrouping) {
        map[g].teams = { ...map[g].teams, ...d.teams };
      } else {
        map[g].players = { ...map[g].players, ...d.players };
      }
    }
  });
  return Object.values(map).sort((a, b) => a.game - b.game);
}, [rawData, isGameAxis, isTeamGrouping]);

  const statDataKey = isTeamGrouping ? 'teams' : 'players';
  const nameMapping = playerNames || teamNames || {};

  const statKeys = useMemo(
    () => Array.from(new Set(data.flatMap((d) => Object.keys(d[statDataKey] || {})))),
    [data, statDataKey]
  );

  const lastIndexes = useMemo(() => {
    const idx = {};
    statKeys.forEach((key) => (idx[key] = -1));
    data.forEach((d, i) => {
      statKeys.forEach((key) => {
        if (d[statDataKey]?.[key] != null) idx[key] = i;
      });
    });
    return idx;
  }, [data, statDataKey, statKeys]);

  const ticks = useMemo(() => {
    if (isGameAxis) return data.map((d) => d.game);
    const step = Math.max(1, Math.ceil(data.length / 10));
    const t = data.filter((_, i) => i % step === 0).map((d) => d.date);
    if (t[t.length - 1] !== data[data.length - 1].date) t.push(data[data.length - 1].date);
    return t;
  }, [data, isGameAxis]);

  // Early return if no data
  if (data.length === 0) {
    return <div>Loading chart...</div>;
  }

  const baseMugSize = 60;
  const halfMug = baseMugSize / 2;

  const renderCustomDot = ({ cx, cy, index, dataKey }) => {
    const defaultDot = <circle cx={cx} cy={cy} r={4} fill="none" />;
    const key = dataKey.split('.')[1];
    if (index !== lastIndexes[key]) return defaultDot;
    const logoUrl = statDataKey === 'players'
      ? `https://assets.nhle.com/mugs/nhl/${season}/${playerInfo[key].team_abb}/${key}.png`
      : `https://assets.nhle.com/logos/nhl/svg/${key}_light.svg`;
    return (
      <g>
        {defaultDot}
        <image
          href={logoUrl}
          x={cx - halfMug}
          y={cy - baseMugSize}
          width={baseMugSize}
          height={baseMugSize}
        />
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={800}>
      <ComposedChart data={data} margin={{ top: 80, right: 20, bottom: 20, left: 50 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <ReferenceLine y={0} stroke="#595959" strokeWidth={1} />
        <XAxis
          type={isGameAxis ? 'number' : 'category'}
          dataKey={isGameAxis ? 'game' : 'date'}
          domain={isGameAxis ? ['dataMin', 'dataMax'] : undefined}
          allowDecimals={false}
          tickFormatter={isGameAxis ? (n) => `#${n}` : formatDate}
          ticks={ticks}
          label={{
            value: isGameAxis ? 'Game #' : 'Date',
            position: 'insideBottom',
            dy: 20,
            textAnchor: 'middle',
          }}
        />
        <YAxis label={<CenteredYAxisLabel value={yAxisLabel} />} />
        <Tooltip shared content={<CustomTooltip nameMapping={nameMapping} groupColors={groupColors} />} />
        {statKeys.length === 1 && (
          <Area
            type="monotone"
            dataKey={`${statDataKey}.${statKeys[0]}`}
            baseValue={0}
            stroke="none"
            fill={groupColors[statKeys[0]].color}
            fillOpacity={0.3}
            connectNulls
          />
        )}
        {statKeys.map((key) => (
          <Line
            key={key}
            type="monotone"
            dataKey={`${statDataKey}.${key}`}
            name={nameMapping[key] || key}
            stroke={groupColors[key].color}
            strokeWidth={activeLine === key ? 8 : 6}
            dot={renderCustomDot}
            activeDot={false}
            connectNulls
            onMouseEnter={() => setActiveLine(key)}
            onMouseLeave={() => setActiveLine(null)}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default SnakeChart;
